import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLanguage } from "../LanguageContext";

type MenuPos = { x: number; y: number };

function isEditableTarget(target: EventTarget | null) {
	if (!(target instanceof Element)) return false;
	return Boolean(
		target.closest("input, textarea, select, [contenteditable='true']"),
	);
}

export default function ContextMenu() {
	const { text } = useLanguage();
	const [pos, setPos] = useState<MenuPos | null>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const open = (event: MouseEvent) => {
			if (isEditableTarget(event.target)) return;
			event.preventDefault();
			setPos({ x: event.clientX, y: event.clientY });
		};

		const close = () => setPos(null);

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};

		window.addEventListener("contextmenu", open);
		window.addEventListener("pointerdown", close);
		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("scroll", close, true);
		window.addEventListener("resize", close);

		return () => {
			window.removeEventListener("contextmenu", open);
			window.removeEventListener("pointerdown", close);
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("scroll", close, true);
			window.removeEventListener("resize", close);
		};
	}, []);

	useLayoutEffect(() => {
		if (!pos || !menuRef.current) return;
		const rect = menuRef.current.getBoundingClientRect();
		const pad = 8;
		const x = Math.min(pos.x, window.innerWidth - rect.width - pad);
		const y = Math.min(pos.y, window.innerHeight - rect.height - pad);
		const next = { x: Math.max(pad, x), y: Math.max(pad, y) };
		if (next.x !== pos.x || next.y !== pos.y) setPos(next);
	}, [pos]);

	if (!pos) return null;

	return (
		<div
			ref={menuRef}
			className="site-context-menu"
			role="menu"
			style={{ left: pos.x, top: pos.y }}
			onPointerDown={(event) => event.stopPropagation()}
		>
			<button
				type="button"
				className="site-context-menu-item"
				role="menuitem"
				onClick={() => setPos(null)}
			>
				{text.site.contextMenu}
			</button>
		</div>
	);
}
